import type { ComponentType, ReactNode } from 'react';

import {
  EventHero, EventDescription, EventLineup, EventTickets, EventOpportunity,
} from './blocks/eventBlocks';
import {
  FooterColumns, LinkList, AnnouncementBar, SponsorStrip,
} from './blocks/sectionBlocks';
import { GlobalSection } from './blocks/GlobalSection';

/**
 * The compiled Block Registry.
 *
 * ── NO CODE COMES FROM THE DATABASE ──────────────────────────────────────────────────
 *
 * A document says `component: "EventHero"`. This file is where that string becomes a
 * React component, and the only place it can. There is no dynamic import, no eval, no
 * component name resolved from a variable path, and no way to add an entry except by
 * editing this file and shipping a build. The database contributes an identifier and some
 * props; everything executable is compiled into the bundle.
 *
 * ── IT IS MIRRORED ON THE SERVER ─────────────────────────────────────────────────────
 *
 * src/cms/blocks.js in the engine describes the same blocks — versions, contexts, props
 * schemas, child rules — and refuses a document that disagrees with it. The two halves
 * are kept in step by scripts/verify-cms-registry.mjs, which fails the build if a block
 * exists on one side only or if their versions, contexts or child rules diverge.
 */

export type BlockContext =
  'event' | 'venue' | 'artist' | 'standalone' | 'global_section';

export interface BlockEntry {
  id: string;
  version: number;
  category: 'layout' | 'event' | 'commerce' | 'reusable' | 'section';
  contexts: BlockContext[];
  /** 'any' | 'none' — mirrors the server manifest. */
  children: 'any' | 'none';
  /**
   * A block whose absence is a commercial failure rather than a cosmetic one. A
   * non-critical block that throws is replaced with nothing and the page carries on; a
   * critical one makes the page fall back, because an event page that quietly renders
   * without its ticket panel looks fine and sells nothing.
   */
  critical?: boolean;
  Live: ComponentType<Record<string, unknown> & { children?: ReactNode }>;
}

// ── Layout blocks ───────────────────────────────────────────────────────────
//
// Structural only. There is no className prop and there never will be: a free-text class
// on a document node is a hole through which configuration reaches the stylesheet.

function Section({ variant = 'plain', children }: { variant?: string; children?: ReactNode }) {
  if (variant === 'main') return <div className="detail__main">{children}</div>;
  if (variant === 'aside') return <aside className="detail__aside">{children}</aside>;
  if (variant === 'hero') return <>{children}</>;
  return <article className="detail">{children}</article>;
}

function TwoColumn({ children }: { children?: ReactNode }) {
  return <div className="detail__layout">{children}</div>;
}

const entries: BlockEntry[] = [
  {
    id: 'Section', version: 1, category: 'layout',
    contexts: ['event', 'venue', 'artist', 'standalone', 'global_section'], children: 'any',
    Live: Section as BlockEntry['Live'],
  },
  {
    id: 'TwoColumn', version: 1, category: 'layout',
    contexts: ['event', 'venue', 'artist', 'standalone', 'global_section'], children: 'any',
    Live: TwoColumn as BlockEntry['Live'],
  },
  {
    id: 'EventHero', version: 1, category: 'event',
    contexts: ['event'], children: 'none',
    Live: EventHero as BlockEntry['Live'],
  },
  {
    id: 'EventDescription', version: 1, category: 'event',
    contexts: ['event'], children: 'none',
    Live: EventDescription as BlockEntry['Live'],
  },
  {
    id: 'EventLineup', version: 1, category: 'event',
    contexts: ['event'], children: 'none',
    Live: EventLineup as BlockEntry['Live'],
  },
  {
    id: 'EventTickets', version: 1, category: 'commerce',
    contexts: ['event'], children: 'none', critical: true,
    Live: EventTickets as BlockEntry['Live'],
  },
  {
    id: 'EventOpportunity', version: 1, category: 'event',
    contexts: ['event'], children: 'none',
    Live: EventOpportunity as BlockEntry['Live'],
  },

  // ── Reusable ────────────────────────────────────────────────────────────────
  //
  // Not valid inside 'global_section', which is the whole of the cycle prevention: a
  // footer cannot contain a footer, so there is no chain to detect. The server manifest
  // says the same thing, and verify-cms-registry.mjs fails the build if they disagree.
  {
    id: 'GlobalSection', version: 1, category: 'reusable',
    contexts: ['event', 'venue', 'artist', 'standalone'], children: 'none',
    Live: GlobalSection as BlockEntry['Live'],
  },

  // ── Global section content ──────────────────────────────────────────────────
  {
    id: 'FooterColumns', version: 1, category: 'section',
    contexts: ['global_section'], children: 'any',
    Live: FooterColumns as BlockEntry['Live'],
  },
  {
    id: 'LinkList', version: 1, category: 'section',
    contexts: ['global_section'], children: 'none',
    Live: LinkList as BlockEntry['Live'],
  },
  {
    id: 'AnnouncementBar', version: 1, category: 'section',
    contexts: ['global_section'], children: 'none',
    Live: AnnouncementBar as BlockEntry['Live'],
  },
  {
    id: 'SponsorStrip', version: 1, category: 'section',
    contexts: ['global_section'], children: 'none',
    Live: SponsorStrip as BlockEntry['Live'],
  },
];

export const BLOCK_REGISTRY: Readonly<Record<string, BlockEntry>> = Object.freeze(
  Object.fromEntries(entries.map((e) => [e.id, e])),
);

export const BLOCK_LIST: readonly BlockEntry[] = Object.freeze(entries);

/**
 * Is losing this block a commercial failure rather than a cosmetic one?
 *
 * THE ONLY LIST. The host reads this rather than keeping its own copy, because two lists
 * would eventually disagree about whether losing the ticket panel matters — and the one
 * that said "no" would be the one running when it happened.
 *
 *   critical      EventTickets — the page sells nothing without it
 *   non-critical  EventHero, EventDescription, EventLineup, EventOpportunity, and the
 *                 layout blocks. Each is worth losing to keep the rest of the page.
 */
export function isCriticalComponent(component: string): boolean {
  return Boolean(lookupBlock(component)?.critical);
}

/** Own-property lookup: a document must not reach `constructor` or `toString`. */
export function lookupBlock(id: unknown): BlockEntry | null {
  if (typeof id !== 'string') return null;
  return Object.hasOwn(BLOCK_REGISTRY, id) ? BLOCK_REGISTRY[id] : null;
}
